import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import BillingPage from "./pages/BillingPage";
import AdminPage from "./pages/AdminPage";
import CamilaAnalyticsPage from "./pages/CamilaAnalyticsPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import PlanSelectionPage from "./pages/PlanSelectionPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import AuthCallback from "./pages/AuthCallback";
import LegalChatPage from "./pages/LegalChatPage";
import KitPage from "./pages/KitPage";
import GarzonPage from "./pages/GarzonPage";
import InmobiliariaPage from "./pages/InmobiliariaPage";
import LegalPage from "./pages/LegalPage";
import StorePage from "./pages/StorePage";
import StoreBotWidgetPage from "./pages/StoreBotWidgetPage";
import StoreBotDocsPage from "./pages/StoreBotDocsPage";
import PartnerDashboardPage from "./pages/PartnerDashboardPage";
import PartnerChatPage from "./pages/PartnerChatPage";
import PartnerRegisterPage from "./pages/PartnerRegisterPage";
import {
  PreLegalAvisoLegalPage,
  PreLegalTermsPage,
  PreLegalPrivacyPage,
} from "./pages/PreLegalLegalPages";
import GarzonBotKitPage from "./pages/kits/GarzonBotKitPage";
import AutoBotKitPage from "./pages/kits/AutoBotKitPage";
import InmoBotKitPage from "./pages/kits/InmoBotKitPage";
import LegalBotKitPage from "./pages/kits/LegalBotKitPage";
import StoreBotKitPage from "./pages/kits/StoreBotKitPage";
import PricingPage from "./pages/PricingPage";
import ProtectedRoute from "./components/ProtectedRoute";
import SupportChat from "./components/SupportChat";
import "@/App.css";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

function AppRouter() {
  const location = useLocation();

  // Scroll to top on every route change UNLESS the URL has a hash (allow
  // anchor navigation like /#pricing to work). React Router doesn't do this
  // automatically and Chrome preserves the previous scroll offset, so kit
  // pages opened from the pricing section would load half-way down.
  React.useEffect(() => {
    if (!location.hash) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, [location.pathname, location.hash]);

  // Detect session_id in URL fragment synchronously during render (not in useEffect)
  // This prevents race conditions with ProtectedRoute checking auth first
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Hide Alex SupportChat on the hidden legal chat and on PreLegal policy pages
  // (Alex is exclusive to AcbotHub — must not appear in the PreLegal vertical)
  const hideSupportChat =
    location.pathname.startsWith('/legal-') ||
    location.pathname.startsWith('/prelegal/') ||
    location.pathname.startsWith('/chat/') ||
    location.pathname.startsWith('/widget/');

  return (
    <>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/plans" element={<ProtectedRoute><PlanSelectionPage /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
        <Route path="/admin/camila-analytics" element={<ProtectedRoute><CamilaAnalyticsPage /></ProtectedRoute>} />
        <Route path="/kit" element={<ProtectedRoute><KitPage /></ProtectedRoute>} />
        <Route path="/garzon" element={<ProtectedRoute><GarzonPage /></ProtectedRoute>} />
        <Route path="/inmobiliaria" element={<ProtectedRoute><InmobiliariaPage /></ProtectedRoute>} />
        <Route path="/kit-legal" element={<ProtectedRoute><LegalPage /></ProtectedRoute>} />
        <Route path="/kit-store" element={<ProtectedRoute><StorePage /></ProtectedRoute>} />
        {/* Public StoreBot chat widget — no auth, served inside embed iframe */}
        <Route path="/widget/storebot/:configId" element={<StoreBotWidgetPage />} />
        {/* Public docs for the embeddable widget */}
        <Route path="/docs/storebot-widget" element={<StoreBotDocsPage /> } />
        <Route path="/partner" element={<ProtectedRoute><PartnerDashboardPage /></ProtectedRoute>} />
        <Route path="/register-partner" element={<PartnerRegisterPage />} />
        {/* Public partner bot chat — accessible without login */}
        <Route path="/chat/:bot_slug" element={<PartnerChatPage />} />
        {/* Hidden legal chat route — intentionally not linked from any navigation */}
        <Route path="/legal-8d1gwmrixnd3gn7s" element={<LegalChatPage />} />
        <Route path="/prelegal/aviso-legal" element={<PreLegalAvisoLegalPage />} />
        <Route path="/prelegal/terminos" element={<PreLegalTermsPage />} />
        <Route path="/prelegal/privacidad" element={<PreLegalPrivacyPage />} />
        <Route path="/kits/garzonbot" element={<GarzonBotKitPage />} />
        <Route path="/kits/autobot" element={<AutoBotKitPage />} />
        <Route path="/kits/inmobot" element={<InmoBotKitPage />} />
        <Route path="/kits/legalbot" element={<LegalBotKitPage />} />
        <Route path="/kits/storebot" element={<StoreBotKitPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {!hideSupportChat && <SupportChat />}
    </>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
            <Toaster position="top-right" />
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
